import { Account, makeAccount } from './data.ts';
import { Storage } from './storage.ts';

export default class StorageInBrowser implements Storage {
  private listFallback: Account[];

  private currentIndexFallback: number;

  private useListFallback: boolean;

  private useCurrentIndexFallback: boolean;

  private readonly LIST_KEY: string;

  private readonly INDEX_KEY: string;

  static init() {
    return new StorageInBrowser();
  }

  constructor() {
    this.LIST_KEY = 'accountsList';
    this.INDEX_KEY = 'accountsCurrentIndex';
    this.listFallback = [];
    this.currentIndexFallback = -1;
    this.useListFallback = false;
    this.useCurrentIndexFallback = false;

    try {
      const parsedJSON = JSON.parse(localStorage.getItem(this.LIST_KEY) as string) || [];
      this.listFallback = Array.isArray(parsedJSON) ? parsedJSON.map(makeAccount) : [];
    } catch {
      this.listFallback = [];
    }

    const parsedIndex = Number(localStorage.getItem(this.INDEX_KEY));
    if (!Number.isNaN(parsedIndex)) {
      this.currentIndexFallback = parsedIndex;
    }

    try {
      if (!localStorage.getItem(this.LIST_KEY)) {
        localStorage.setItem(this.LIST_KEY, JSON.stringify(this.listFallback));
      }
    } catch {
      this.useListFallback = true;
    }

    try {
      if (!localStorage.getItem(this.INDEX_KEY)) {
        localStorage.setItem(this.INDEX_KEY, String(this.currentIndexFallback));
      }
    } catch {
      this.useCurrentIndexFallback = true;
    }
  }

  getCurrentIndex(): number {
    if (this.useCurrentIndexFallback) {
      return this.currentIndexFallback;
    }

    const parsedIndex = Number(localStorage.getItem(this.INDEX_KEY));
    if (Number.isNaN(parsedIndex)) {
      return this.currentIndexFallback;
    }

    this.currentIndexFallback = parsedIndex;
    return parsedIndex;
  }

  setCurrentIndex(index: number) {
    this.currentIndexFallback = index;
    try {
      localStorage.setItem(this.INDEX_KEY, String(index));
      this.useCurrentIndexFallback = false;
    } catch {
      this.useCurrentIndexFallback = true;
    }
  }

  getList(): Account[] {
    if (this.useListFallback) {
      return this.listFallback;
    }

    try {
      const storedList = localStorage.getItem(this.LIST_KEY);
      if (storedList === null) {
        return this.listFallback;
      }

      const parsedJSON = JSON.parse(storedList) || [];
      this.listFallback = Array.isArray(parsedJSON) ? parsedJSON.map(makeAccount) : [];
      return this.listFallback;
    } catch {
      return this.listFallback;
    }
  }

  setList(list: Account[]) {
    this.listFallback = list.map((account) => makeAccount({ ...account, email: account.email || '' }));
    try {
      localStorage.setItem(this.LIST_KEY, JSON.stringify(this.listFallback));
      this.useListFallback = false;
    } catch {
      this.useListFallback = true;
    }
  }
}
